/* JOY product families: navigation only; never rewrites products, variants or carts. */
(() => {
  'use strict';
  const normal = (value) => String(value ?? '').normalize('NFKC').toLowerCase().replace(/[’‘`]/g, "'").replace(/\s+/g, ' ').trim();
  const ukrainian = typeof document !== 'undefined' && document.documentElement.lang.toLowerCase().startsWith('uk');
  const ukTypes = {"БАЗА": "BASE", "ТОП": "TOP", "ГЕЛЬ-ЛАК": "GEL POLISH", "МОДЕЛЮЮЧИЙ ГЕЛЬ": "BUILDER GEL", "КОНСТРУЮЮЧИЙ ГЕЛЬ": "BUILDER GEL", "ЖЕЛЕ-ГЕЛЬ": "JELLY GEL", "ДЕЛЕЙ-ГЕЛЬ": "JELLY GEL", "АКРИГЕЛЬ": "ACRYGEL", "МЕТАЛЕВИЙ ГЕЛЬ": "METAL GEL", "ГЕЛЕВА ПАСТА": "GEL PASTE", "ГЕЛЬ-ПАСТА": "GEL PASTE", "ГЕЛЬ-ФАРБА": "GEL PAINT", "ДОГЛЯД ЗА РУКАМИ": "HAND CARE", "ПІДГОТОВКА": "PREP", "ДОГЛЯД ЗА КУТИКУЛОЮ": "CUTICLE CARE", "ВИДАЛЯТИ": "REMOVER", "РІДКІ БЛИСКІТКИ": "LIQUID GLITTER", "РІДКИЙ КОНСТРУЮЮЧИЙ ГЕЛЬ": "LIQUID BUILDER GEL", "РІДИНИ": "LIQUIDS", "ГЛІТЕР-ГЕЛЬ": "GLITTER GEL", "ПОЛІГЕЛЬ": "POLYGEL", "ТВЕРДИЙ ГЕЛЬ": "HARD GEL", "ЛАК ДЛЯ НІГТІВ": "NAIL POLISH", "ДОГЛЯД ЗА НІГТЯМИ": "NAIL CARE", "МАНІКЮРНИЙ СПА": "MANICURE SPA", "МАНІКЮР SPA": "MANICURE SPA", "ЗАСІБ ДЛЯ ОЧИЩЕННЯ": "CLEANSER", "ҐРУНТОВКА": "PRIMER", "ГЕЛЬ У ФЛАКОНІ": "GEL IN BOTTLE", "ДОГЛЯД ЗА НІГТЯМИ ТА КУТИКУЛОЮ": "NAIL & CUTICLE CARE", "ДОГЛЯД ЗА ШКІРОЮ": "SKIN CARE", "ДОГЛЯД ЗА СТОПАМИ": "FOOT CARE"};
  const canonicalType = (value) => { const type = String(value || '').toUpperCase().trim(); return ukrainian ? (ukTypes[type] || type) : type; };
  const canonicalUnits = (value) => ukrainian ? String(value || '').replace(/(\d)\s*мл(?=$|[\s,;#])/gi, '$1 ml').replace(/(\d)\s*(?:гр|г)(?=$|[\s,;#])/gi, '$1 g').replace(/(\d)\s*л(?=$|[\s,;#])/gi, '$1 l') : value;
  const compact = (value) => normal(value).replace(/[\s_,]+/g, ' ').trim();
  const allowedTypes = new Set(['BASE', 'BUILDER GEL', 'LIQUID BUILDER GEL', 'GEL IN BOTTLE', 'GEL POLISH', 'NAIL POLISH', 'TOP', 'ACRYGEL', 'POLYGEL', 'HARD GEL', 'JELLY GEL', 'GLITTER GEL', 'LIQUID GLITTER', 'GEL PAINT', 'GEL PASTE', 'METAL GEL', 'LIQUIDS', 'PREP', 'REMOVER', 'PRIMER', 'CLEANSER', 'CUTICLE CARE', 'HAND CARE', 'FOOT CARE', 'NAIL & CUTICLE CARE', 'NAIL CARE', 'SKIN CARE', 'MANICURE SPA', 'PODOLOGY CARE']);
  const colorTypes = new Set(['BASE', 'BUILDER GEL', 'LIQUID BUILDER GEL', 'GEL IN BOTTLE', 'GEL POLISH', 'NAIL POLISH', 'TOP', 'ACRYGEL', 'POLYGEL', 'HARD GEL', 'JELLY GEL', 'GLITTER GEL', 'LIQUID GLITTER', 'GEL PAINT', 'GEL PASTE', 'METAL GEL']);
  const brands = new Set(['dnka', 'lunamoon', 'youarecute', 'touch', 'kinetics', 'shelly', 'staleks']);
  const clean = (text) => compact(text).replace(/^[\s,"'()\-]+|[\s,"'()\-]+$/g, '').replace(/\s*,\s*/g, ' ').replace(/\s+/g, ' ').trim();
  const volumePattern = /\b(\d+(?:[.,]\d+)?)\s*(ml|gr|g|l)\b/gi;
  const volume = (token) => {
    const match = String(canonicalUnits(token) || '').trim().match(/^(\d+(?:[.,]\d+)?)\s*(ml|gr|g|l)$/i);
    if (!match) return null;
    const value = Number(match[1].replace(',', '.'));
    const unit = match[2].toLowerCase() === 'gr' ? 'g' : match[2].toLowerCase();
    if (!(value > 0)) return null;
    return { key: `${unit === 'l' ? value * 1000 : value}:${unit === 'l' ? 'ml' : unit}`, value: unit === 'l' ? value * 1000 : value, unit: unit === 'l' ? 'ml' : unit, label: `${value.toLocaleString('de-DE')} ${unit}` };
  };
  function describe(product) {
    const vendor = normal(product?.vendor), type = canonicalType(product?.type);
    if (!brands.has(vendor) || !allowedTypes.has(type) || Number(product.variant_count || 1) !== 1) return null;
    const verified = {
      'dnka-ftbrd12': ['dnka', 'BASE', 'rubber base neue formel', 12, '12 ml', '12:ml'],
      'dnka-ftbrd30': ['dnka', 'BASE', 'rubber base neue formel', 30, '30 ml', '30:ml'],
      'dnka-ftbrd30p': ['dnka', 'BASE', 'rubber base neue formel', 30, '30 ml Pump', '30:ml:pump'],
      'staleks-rot-30': ['staleks', 'PODOLOGY CARE', 'skin regenerating oil treat.s', 30, '30 ml', '30:ml'],
      'staleks-rot-50': ['staleks', 'PODOLOGY CARE', 'skin regenerating oil treat.s', 50, '50 ml', '50:ml']
    }[product.handle];
    if (verified && vendor === verified[0] && type === verified[1]) {
      return { family: verified.slice(0, 3).join('|'), shadeKey: '', shade: '', canShowShades: false,
        size: { value: verified[3], label: verified[4], key: verified[5], unit: 'ml' }, product };
    }
    if (vendor === 'staleks') return null;
    let title = String(canonicalUnits(product.title) || '').normalize('NFKC');
    const found = [...title.matchAll(volumePattern)];
    if (found.length > 1) return null; // Kits and ambiguous mixed pack sizes stay with the existing picker.
    const titleVolume = found.length ? volume(found[0][0]) : null;
    const metaVolume = volume(product.volume);
    if (titleVolume && metaVolume && titleVolume.key !== metaVolume.key) return null;
    const size = titleVolume || metaVolume;
    if (!size) return null;
    title = title.replace(volumePattern, ' ');
    const escapedVendor = vendor.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    title = title.replace(new RegExp(`\\b${escapedVendor}\\b`, 'ig'), '');
    if (vendor === 'touch') title = title.replace(/^\s*TOUGH\b/i, '');
    title = clean(title);
    if (!title) return null;
    let family = title, shade = '', shadeKey = '', canShowShades = false;
    if (colorTypes.has(type)) {
      if (vendor === 'kinetics') {
        let match = null;
        const patterns = [
          ['shield gel polish', /^shield\s+(?:gellack|gel\s+polish)\s+(.+?)\s*#(\d+)$/i],
          ['solargel nail polish', /^solar(?:gel\s+nail\s+polish|gellack|gel\s+polish)\s+(.+?)\s*#(\d+)$/i],
          ['gel in bottle', /^gel in bottle\s+(.+?)\s*#(\d+)$/i],
          ['medium jelly gel', /^medium jelly gel\s*#(\d+)\s+(.+)$/i],
          ['jelly gel strong', /^jelly gel strong\s*#(\d+)\s+(.+)$/i]
        ];
        for (const [key, pattern] of patterns) {
          match = title.match(pattern);
          if (!match) continue;
          const numberFirst = key.includes('jelly');
          const number = numberFirst ? match[1] : match[2];
          const name = numberFirst ? match[2] : match[1];
          family = key; shadeKey = `#${Number(number)}|${clean(name)}`; shade = `#${number} ${name}`; canShowShades = true; break;
        }
      } else if (vendor === 'touch') {
        title = title.replace(/^top cover\b/, 'cover top');
        family = title;
        let match = title.match(/^(.+?)\s*(?:\s|-)\s*(\d{1,4})$/);
        if (match) {
          family = clean(match[1]); shadeKey = `#${Number(match[2])}`; shade = match[2]; canShowShades = true;
        } else {
          const named = title.match(/^(cover top|builder gel|polygel|liquid polygel)\s+(.+)$/);
          if (named) {
            const knownNames = new Set(['butter','blossom','bronze','camel','cloud','iris','lagoon','milky','milky pink','milky rosa','rose water','satin','taffy','tint','walnut','warm','zefir','beige','clear','milk','nude','peony','pink','rosa','aqua','blau lagoon','blue lagoon','electric','fuchsia','hot rot','hot red','neo light','ruby','sunny yellow','toxic grün','toxic green']);
            if (knownNames.has(named[2])) { family = named[1]; shade = named[2]; shadeKey = named[2]; canShowShades = true; }
          }
        }
      } else {
        const match = title.match(/^(.*?)\s*(?:#|\bnr\.?\s*|№)\s*(\d{1,5})\s*(.*)$/i);
        if (match && clean(match[1])) {
          family = clean(match[1]); shadeKey = `#${Number(match[2])}|${clean(match[3])}`; shade = `${match[2]}${match[3] ? ` ${match[3]}` : ''}`; canShowShades = true;
          // DNKa's named Cat Eye series are not silently merged with one another.
          if (vendor === 'dnka' && family.includes('eye')) {
            const suffix = clean(match[3]);
            if (['80\'s', 'bohemian', '1000 & 1 nights', 'silk', ''].includes(suffix)) family += ` / ${suffix || 'classic'}`;
            else { family = title; shadeKey = ''; shade = ''; canShowShades = false; }
          }
        }
      }
    }
    if (!canShowShades) { family = title; shadeKey = ''; shade = ''; }
    return { family: `${vendor}|${type}|${family}`, shadeKey, shade, size, canShowShades, product };
  }
  function choose(current, products) {
    const active = describe(current);
    if (!active) return { shades: [], volumes: [], active: null };
    const described = products.map(describe).filter(Boolean).filter((item) => item.family === active.family);
    if (!described.some((item) => String(item.product.id) === String(current.id))) described.push(active);
    const keyed = new Map();
    for (const item of described) {
      const key = `${item.shadeKey}|${item.size.key}`;
      if (!keyed.has(key)) keyed.set(key, []);
      const list = keyed.get(key);
      if (!list.some((old) => String(old.product.id) === String(item.product.id))) list.push(item);
    }
    if ((keyed.get(`${active.shadeKey}|${active.size.key}`) || []).length !== 1) return { active, shades: [], volumes: [] };
    const unambiguous = [...keyed.values()].filter((list) => list.length === 1).flat();
    const collator = new Intl.Collator('de', { numeric: true, sensitivity: 'base' });
    const shades = active.canShowShades ? unambiguous.filter((item) => item.size.key === active.size.key && item.canShowShades).sort((a, b) => collator.compare(a.shade, b.shade)) : [];
    const volumes = unambiguous.filter((item) => item.shadeKey === active.shadeKey && item.size.unit === active.size.unit).sort((a, b) => a.size.value - b.size.value);
    return { active, shades: shades.length > 1 ? shades : [], volumes: volumes.length > 1 ? volumes : [] };
  }
  globalThis.JoyProductFamilies = Object.freeze({ describe, choose });
  if (typeof document === 'undefined' || typeof customElements === 'undefined' || customElements.get('joy-product-options')) return;
  const fetches = new Map();
  const fetchText = async (url) => {
    const abort = new AbortController();
    const timer = setTimeout(() => abort.abort(), 8000);
    try { const response = await fetch(url, { signal: abort.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } }); if (!response.ok) throw new Error('Options unavailable'); return await response.text(); }
    finally { clearTimeout(timer); }
  };
  const fetchProducts = (searchPath, vendor, type) => {
    const key = [searchPath, vendor, type].join('|');
    if (fetches.has(key)) return fetches.get(key);
    const promise = (async () => {
      const quote = (value) => `"${String(value).replace(/(["\\])/g, '\\$1')}"`;
      const url = new URL(searchPath, location.origin);
      if (url.origin !== location.origin) throw new Error('Not a storefront URL');
      // Compare canonical catalog names; translated family names can vary between products.
      // Only Ukrainian product navigation uses the primary-language read endpoint.
      if (ukrainian && url.pathname.startsWith('/uk/')) url.pathname = url.pathname.slice(3);
      url.searchParams.set('section_id', 'joy-product-options-data');
      url.searchParams.set('type', 'product');
      url.searchParams.set('q', `vendor:${quote(vendor)} AND product_type:${quote(ukrainian ? canonicalType(type) : type)}`);
      url.searchParams.set('options[unavailable_products]', 'show');
            let result = [];
      for (let page = 1; page <= 8; page++) {
        url.searchParams.set('page', String(page));
        const html = await fetchText(url);
        const json = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-joy-options-data]');
        if (!json) throw new Error('Options section missing');
        const data = JSON.parse(json.textContent);
        if (!Array.isArray(data.items) || Number(data.pages) > 8) throw new Error('Options data is incomplete');
        result.push(...data.items.filter((item) => normal(item.vendor) === normal(vendor) && (ukrainian ? canonicalType(item.type) === canonicalType(type) : normal(item.type) === normal(type))));
        if (page >= Number(data.pages)) break;
      }
      return result;
    })();
    fetches.set(key, promise);
    promise.catch(() => fetches.delete(key));
    return promise;
  };
  class JoyProductOptions extends HTMLElement {
    static serial = 0;
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.hidden = true;
      try { this.labels = JSON.parse(this.querySelector('[data-labels]').textContent); } catch (_) { return; }
      this.expanded = this.querySelector('[data-expanded]');
      this.expanded.id = `JoyShades-${this.dataset.productId}-${++JoyProductOptions.serial}`;
      this.more = this.querySelector('[data-more]');
      this.more.setAttribute('aria-controls', this.expanded.id);
      this.onMore = () => { this.expanded.hidden = !this.expanded.hidden; this.querySelector('[data-shade-list]').hidden = !this.expanded.hidden; this.more.setAttribute('aria-expanded', String(!this.expanded.hidden)); this.more.textContent = this.expanded.hidden ? this.labels.all : this.labels.less; if (!this.expanded.hidden) this.querySelector('[data-filter]').focus({ preventScroll: true }); };
      this.onFilter = (event) => { const text = normal(event.target.value); let found = 0; for (const link of this.querySelectorAll('[data-all-shades] > a')) { link.hidden = !normal(link.dataset.name).includes(text); if (!link.hidden) found++; } this.querySelector('[data-empty]').hidden = found !== 0; };
      this.more.addEventListener('click', this.onMore);
      this.querySelector('[data-filter]').addEventListener('input', this.onFilter);
      const supported = brands.has(normal(this.dataset.vendor)) && allowedTypes.has(ukrainian ? canonicalType(this.dataset.type) : String(this.dataset.type).toUpperCase());
      if (!supported) return;
      this.load();
    }
    disconnectedCallback() { this.ready = false; this.more?.removeEventListener('click', this.onMore); this.querySelector('[data-filter]')?.removeEventListener('input', this.onFilter); }
    async load() {
      try {
        const products = await fetchProducts(this.dataset.search, this.dataset.vendor, this.dataset.type);
        if (!this.isConnected) return;
        const current = products.find((p) => String(p.id) === this.dataset.productId);
        if (!current) return;
        this.selection = choose(current, products);
        if (this.selection.volumes.length) { this.querySelector('[data-volumes]').hidden = false; this.querySelector('[data-volume-list]').replaceChildren(...this.selection.volumes.map((p) => this.link(p, 'volume'))); }
        if (this.selection.shades.length) {
          this.querySelector('[data-shades]').hidden = false;
          this.querySelector('[data-current-shade]').textContent = `· ${this.selection.active.shade}`;
          let initial = this.selection.shades.slice(0, 8);
          const currentOption = this.selection.shades.find((p) => String(p.product.id) === this.dataset.productId);
          if (currentOption && !initial.includes(currentOption)) initial = [...initial.slice(0, 7), currentOption];
          this.querySelector('[data-shade-list]').replaceChildren(...initial.map((p) => this.link(p, 'shade')));
          if (this.selection.shades.length > 8) { this.more.hidden = false; this.querySelector('[data-all-shades]').replaceChildren(...this.selection.shades.map((p) => this.link(p, 'shade'))); }
        }
        this.hidden = !this.selection.volumes.length && !this.selection.shades.length;
      } catch (_) { this.hidden = true; } // Optional navigation must never interrupt the purchase path.
    }
    link(item, kind) {
      const p = item.product, link = document.createElement('a');
      // URLs and product media come from Liquid, never from shopper-controlled HTML.
      const url = new URL(p.url, location.origin);
      if (url.origin !== location.origin || !url.pathname.includes('/products/')) return document.createDocumentFragment();
      if (ukrainian && !url.pathname.startsWith('/uk/')) url.pathname = '/uk' + url.pathname;
      link.href = url.pathname + url.search;
      link.className = `joy-poptions__${kind}`;
      const selected = String(p.id) === this.dataset.productId;
      if (selected) link.setAttribute('aria-current', 'page');
      if (p.available === false) link.classList.add('is-unavailable');
      const label = kind === 'volume' ? item.size.label : item.shade;
      link.dataset.name = `${label} ${p.title}`;
      link.title = `${p.title}${p.available === false ? ` — ${this.labels.soldout}` : ''}`;
      link.setAttribute('aria-label', link.title);
      if (kind === 'shade' && p.image) {
        const imageUrl = new URL(p.image, location.origin);
        if (imageUrl.protocol === 'https:' && (imageUrl.hostname === 'cdn.shopify.com' || imageUrl.origin === location.origin)) {
          const image = document.createElement('img'); image.src = imageUrl.href; image.alt = ''; image.width = 48; image.height = 48; image.loading = 'lazy'; image.decoding = 'async'; link.append(image);
        }
      }
      const caption = document.createElement('span'); caption.textContent = label; link.append(caption);
      return link;
    }
  }
  customElements.define('joy-product-options', JoyProductOptions);
})();
/* Reads only the history already maintained by the original Hyper theme. */
if (typeof customElements !== 'undefined' && !customElements.get('joy-recently-viewed')) {
  customElements.define('joy-recently-viewed', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => { if (entries.some(e => e.isIntersecting)) { this.observer.disconnect(); this.load(); } }, { rootMargin: '600px 0px' });
        this.observer.observe(this);
      } else { this.load(); }
    }
    disconnectedCallback() { this.observer?.disconnect(); this.abort?.abort(); }
    async load() {
      try {
        const stored = JSON.parse(localStorage.getItem('hypertheme:recently-viewed') || '[]');
        if (!Array.isArray(stored)) { this.remove(); return; }
        const ids = [...new Set(stored.map(String).filter(x => /^\d{1,16}$/.test(x) && x !== this.dataset.productId))].slice(0, Math.min(6, Math.max(2, Number(this.dataset.limit) || 6)));
        if (!ids.length) { this.remove(); return; }
        const url = new URL(this.dataset.url, location.origin);
        if (url.origin !== location.origin) { this.remove(); return; }
        url.searchParams.set('section_id', this.dataset.sectionId);
        url.searchParams.set('type', 'product');
        url.searchParams.set('q', ids.map(id => `id:${id}`).join(' OR '));
        this.abort = new AbortController();
        const timer = setTimeout(() => this.abort.abort(), 8000);
        let response, text;
        try { response = await fetch(url, { signal: this.abort.signal, credentials: 'same-origin' }); if (!response.ok) throw new Error('History unavailable'); text = await response.text(); }
        finally { clearTimeout(timer); }
        if (!this.isConnected) return;
        const content = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-joy-recent-content]');
        if (!content || !content.querySelector('.product-card')) { this.remove(); return; }
        content.hidden = false;
        this.replaceChildren(document.importNode(content, true));
        this.dispatchEvent(new CustomEvent('recommendations:loaded', { bubbles: true }));
      } catch (_) { this.remove(); }
    }
  });
}
/* Mount only new nodes. Existing templates, blocks, prices and buttons are not replaced. */
if (typeof document !== 'undefined') {
  const mountJoyProductTools = () => {
    const template = document.getElementById('JoyProductTools');
    if (!(template instanceof HTMLTemplateElement)) return;
    const info = [...document.querySelectorAll('#MainContent .product__info-container')].find(node => node.querySelector('h1.product__title'));
    if (!info || info.querySelector('joy-product-options')) return;
    try {
      // A native combined-listing swap must not receive the previous product's facts.
      if (new URL(info.dataset.url, location.origin).pathname !== new URL(template.dataset.productUrl, location.origin).pathname) return;
    } catch (_) { return; }
    const buy = info.querySelector('.product__block--buy_buttons');
    const picker = info.querySelector('.product__block--variant_picker') || buy;
    if (!buy || !picker) return;
    const copy = template.content.cloneNode(true);
    const options = copy.querySelector('[data-joy-options-source]')?.firstElementChild;
    const facts = copy.querySelector('[data-joy-facts-source]')?.firstElementChild;
    const reviews = copy.querySelector('[data-joy-reviews-source]')?.firstElementChild;
    if (options) picker.before(options);
    if (facts) buy.before(facts);
    if (reviews) info.querySelector('h1.product__title').after(reviews);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mountJoyProductTools, { once: true });
  else mountJoyProductTools();
  document.addEventListener('shopify:section:load', mountJoyProductTools);
  document.addEventListener('variant:changed', mountJoyProductTools);
}
